import { eventBus } from '../core/EventBus';
import { ENEMY_CONFIGS } from '../config/enemies';
import { Pathfinder } from './Pathfinder';
import { Grid } from './Grid';
import { Enemy } from '../entities/Enemy';
import type { EnemyConfig, SpawnMode, Vec2 } from '../types';

interface SpawnOptions {
  spawnIndex?: number;
  spawnMode?: SpawnMode;
  position?: Vec2;
  hpMultiplier?: number;
  speedMultiplier?: number;
  rewardMultiplier?: number;
}

export class EnemyManager {
  private enemies: Enemy[] = [];
  private nextSpawnIndex = 0;

  constructor(
    private pathfinder: Pathfinder,
    private grid: Grid,
  ) {
    this.bindEvents();
  }

  private bindEvents(): void {
    eventBus.on('enemy:healPulse', ({ source, x, y, radius, amount }: {
      source: Enemy;
      x: number;
      y: number;
      radius: number;
      amount: number;
    }) => {
      for (const enemy of this.enemies) {
        if (enemy === source || enemy.hp <= 0 || enemy.reachedCore) continue;
        const dist = Math.hypot(enemy.x - x, enemy.y - y);
        if (dist <= radius) {
          enemy.heal(amount);
          eventBus.emit('effect:hit', { x: enemy.x, y: enemy.y, color: '#66bb6a' });
        }
      }
    });

    eventBus.on('enemy:killed', ({ enemy }: { enemy: Enemy }) => {
      if (enemy.config.id === 'bomber') {
        eventBus.emit('enemy:bomberExploded', {
          x: enemy.x,
          y: enemy.y,
          radius: 1.8,
          towerStunDuration: 2.0,
        });
      }
    });
  }

  public clear(): void {
    this.enemies = [];
    this.nextSpawnIndex = 0;
  }

  public getEnemies(): Enemy[] {
    return this.enemies;
  }

  public spawnEnemy(type: string, options: SpawnOptions = {}): void {
    const baseConfig = ENEMY_CONFIGS[type];
    if (!baseConfig) return;

    const selected = options.position
      ? (baseConfig.flying ? this.getFlightPathFromPosition(options.position) : this.getPathFromPosition(options.position))
      : this.getSpawnPath(options.spawnMode ?? 'roundRobin', options.spawnIndex);

    if (!selected || selected.path.length === 0) {
      console.warn('No valid path for enemy spawn');
      return;
    }

    const config: EnemyConfig = {
      ...baseConfig,
      hp: Math.max(1, Math.round(baseConfig.hp * (options.hpMultiplier ?? 1))),
      speed: baseConfig.speed * (options.speedMultiplier ?? 1),
      reward: Math.max(1, Math.round(baseConfig.reward * (options.rewardMultiplier ?? 1))),
    };

    const path = config.flying
      ? [selected.spawn, selected.path[selected.path.length - 1]]
      : selected.path;

    const enemy = new Enemy(config, path);
    this.enemies.push(enemy);
  }

  private getSpawnPath(mode: SpawnMode, requestedIndex?: number): { spawn: Vec2; path: Vec2[] } | undefined {
    const candidates = [...this.pathfinder.getAllPaths().entries()]
      .filter(([, path]) => path.length > 0)
      .map(([spawn, path]) => ({ spawn, path }));
    if (candidates.length === 0) return undefined;

    let index = 0;
    if (requestedIndex !== undefined) {
      index = this.normalizeIndex(requestedIndex, candidates.length);
    } else if (mode === 'random') {
      index = Math.floor(Math.random() * candidates.length);
    } else if (mode === 'roundRobin') {
      index = this.nextSpawnIndex % candidates.length;
      this.nextSpawnIndex = (this.nextSpawnIndex + 1) % candidates.length;
    }
    return candidates[index];
  }

  private getPathFromPosition(position: Vec2): { spawn: Vec2; path: Vec2[] } | undefined {
    const start = { x: Math.floor(position.x), y: Math.floor(position.y) };
    let best: Vec2[] = [];
    for (const core of this.grid.getCores()) {
      const path = this.pathfinder.getPath(start, core);
      if (path.length > 0 && (best.length === 0 || path.length < best.length)) best = path;
    }
    return best.length > 0 ? { spawn: start, path: best } : undefined;
  }

  private getFlightPathFromPosition(position: Vec2): { spawn: Vec2; path: Vec2[] } | undefined {
    const start = { x: Math.floor(position.x), y: Math.floor(position.y) };
    const cores = this.grid.getCores();
    if (cores.length === 0) return undefined;
    const core = cores.reduce((best, candidate) => {
      const bestDistance = Math.hypot(best.x - position.x, best.y - position.y);
      const candidateDistance = Math.hypot(candidate.x - position.x, candidate.y - position.y);
      return candidateDistance < bestDistance ? candidate : best;
    }, cores[0]);
    return { spawn: start, path: [start, core] };
  }

  private normalizeIndex(index: number, length: number): number {
    return ((Math.floor(index) % length) + length) % length;
  }

  public update(dt: number): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      enemy.update(dt);

      const terrain = this.grid.getTerrainEffect(Math.floor(enemy.x), Math.floor(enemy.y));
      enemy.applyTerrainEffect(terrain, dt);

      if (enemy.reachedCore) {
        eventBus.emit('enemy:reachedCore', { enemy });
        this.enemies.splice(i, 1);
      } else if (enemy.hp <= 0) {
        this.enemies.splice(i, 1);
      }
    }
  }

  public getConfig(id: string): EnemyConfig | undefined {
    return ENEMY_CONFIGS[id];
  }

  public getAllConfigs(): Record<string, EnemyConfig> {
    return ENEMY_CONFIGS;
  }
}
